import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchEnvs } from '@/store/slices/envsSlice';
import { getYoutubeAuthUrl, getTwitchAuthUrl } from '@/lib/utils/authUrls';

const COOKIE_NAME = 'usersession.sid';

/**
 * 인증 헬퍼를 표시할 호스트 목록 — 로컬 개발 환경만 포함.
 * v2 서버(influencer.ggcontent.com, influencer2.ggcontent.com)에 배포된
 * 이후에는 표시하지 않는다.
 */
const ALLOWED_HOSTS = [
  'localhost',
];

function isAuthHelperVisible(): boolean {
  return ALLOWED_HOSTS.some((h) => window.location.hostname === h);
}

/** localhost에서는 Vite 미들웨어, 그 외 도메인에서는 document.cookie로 세션 적용 */
function isLocalhost(): boolean {
  return window.location.hostname === 'localhost';
}

type Lang = 'ko' | 'en';

const TEXT = {
  ko: {
    title: 'Dev Auth Guide',
    desc: '아래 절차로 인증 세션을 설정할 수 있습니다.',
    stepLogin: (
      <>
        아래 버튼 또는 로그인 페이지에서 소셜 로그인을 수행합니다. <em>(새 탭에서 열림)</em>
      </>
    ),
    stepGoogleWarning: (
      <>
        YouTube 로그인 시 Google의{' '}
        <strong>"Apps not verified"</strong> 경고가 표시됩니다.
        <br />
        <strong>"Advanced"</strong> (또는 "고급") 링크를 클릭한 뒤{' '}
        <strong>"Go to gmchanger-dev.herokuapp.com (unsafe)"</strong>를 클릭하여
        진행합니다.
      </>
    ),
    stepCopy: (
      <>
        로그인 완료 후 dev 서버의 브라우저 개발자 도구
        <br />
        <strong>Mac: Cmd + Option + I</strong>,{' '}
        <strong>Windows: F12</strong> → Application → Cookies에서{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">
          {COOKIE_NAME}
        </code>{' '}
        값을 복사합니다.
      </>
    ),
    stepPaste: (
      <>
        아래 입력란에 붙여넣고 <strong>"세션 적용"</strong>을 클릭합니다.
      </>
    ),
    note: (
      <>
        <strong>참고:</strong> 세션 적용 시{' '}
        <code className="bg-amber-100 px-1 rounded">{COOKIE_NAME}</code>를
        쿠키로 설정합니다. 이후 모든 API 요청에 이 쿠키가 자동으로
        포함됩니다.
      </>
    ),
    sessionPlaceholder: `${COOKIE_NAME} 쿠키 값 붙여넣기`,
    apply: '세션 적용',
    checking: '확인 중...',
    checkSession: '현재 세션 확인',
    authSuccess: '인증 성공!',
    loggedInAs: '로그인 사용자',
    close: '닫기',
    noSession: '현재 세션에 로그인된 사용자가 없습니다.',
    apiFailed: 'API 호출 실패.',
    emptyInput: '세션 값을 입력해주세요.',
  },
  en: {
    title: 'Dev Auth Guide',
    desc: 'Follow the steps below to set up an authenticated session.',
    stepLogin: (
      <>
        Click the buttons below or on the login page to perform social login.{' '}
        <em>(opens in new tab)</em>
      </>
    ),
    stepGoogleWarning: (
      <>
        When logging in with YouTube, Google will show an{' '}
        <strong>"Apps not verified"</strong> warning.
        <br />
        Click <strong>"Advanced"</strong>, then click{' '}
        <strong>"Go to gmchanger-dev.herokuapp.com (unsafe)"</strong> to proceed.
      </>
    ),
    stepCopy: (
      <>
        After logging in, open the browser DevTools on the dev server
        <br />
        <strong>Mac: Cmd + Option + I</strong>,{' '}
        <strong>Windows/Linux: F12</strong> → Application → Cookies and copy the{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">
          {COOKIE_NAME}
        </code>{' '}
        value.
      </>
    ),
    stepPaste: (
      <>
        Paste it into the input below and click{' '}
        <strong>"Apply Session"</strong>.
      </>
    ),
    note: (
      <>
        <strong>Note:</strong> Sets{' '}
        <code className="bg-amber-100 px-1 rounded">{COOKIE_NAME}</code> as a
        cookie. All subsequent API requests will automatically include it.
      </>
    ),
    sessionPlaceholder: `Paste ${COOKIE_NAME} cookie value`,
    apply: 'Apply Session',
    checking: 'Checking...',
    checkSession: 'Check Current Session',
    authSuccess: 'Auth Success!',
    loggedInAs: 'Logged in as',
    close: 'Close',
    noSession: 'No authenticated user in current session.',
    apiFailed: 'API call failed.',
    emptyInput: 'Please enter a session value.',
  },
} as const;

/**
 * 플로팅 인증 가이드 + 세션 적용
 * 로컬 개발 환경(localhost)에서만 렌더링한다.
 * - 로컬: Vite 미들웨어(/__dev-auth-callback)로 httpOnly 쿠키 설정
 * - 배포: document.cookie 로 직접 설정 (현재 허용 호스트 없음, 향후 확장용)
 * v2 서버(influencer.ggcontent.com, influencer2.ggcontent.com) 및 staging은
 * 직접 OAuth가 가능하므로 제외.
 */
export default function DevAuthGuide() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState<Lang>('ko');
  const [sessionValue, setSessionValue] = useState('');
  const [status, setStatus] = useState<'idle' | 'checking' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((s) => s.envs.currentUser);

  // 외부에서 다이얼로그 열기 (Login 페이지 등)
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('open-dev-auth-guide', handler);
    return () => window.removeEventListener('open-dev-auth-guide', handler);
  }, []);

  // /__dev-auth-callback 리다이렉트 후 자동 감지
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('_dev_auth') === 'success') {
      setOpen(true);
      dispatch(fetchEnvs());
      // URL에서 파라미터 제거
      params.delete('_dev_auth');
      const clean = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (clean ? `?${clean}` : ''),
      );
    }
  }, [dispatch]);

  useEffect(() => {
    if (currentUser && open) {
      setStatus('success');
    }
  }, [currentUser, open]);

  if (!isAuthHelperVisible()) return null;

  const t = TEXT[lang];

  const applySession = () => {
    if (!sessionValue.trim()) {
      setErrorMsg(t.emptyInput);
      return;
    }
    setStatus('checking');
    setErrorMsg('');

    if (isLocalhost()) {
      // localhost: Vite 미들웨어가 httpOnly 쿠키를 설정
      window.location.href = `/__dev-auth-callback?session=${encodeURIComponent(sessionValue.trim())}`;
    } else {
      // 배포 도메인: document.cookie 로 직접 설정 후 fetchEnvs 로 검증
      document.cookie = `${COOKIE_NAME}=${sessionValue.trim()}; Path=/; SameSite=Lax`;
      dispatch(fetchEnvs())
        .unwrap()
        .then((data) => {
          if (data.currentUser) {
            setStatus('success');
          } else {
            setStatus('error');
            setErrorMsg(t.noSession);
          }
        })
        .catch(() => {
          setStatus('error');
          setErrorMsg(t.apiFailed);
        });
    }
  };

  const checkCurrentSession = () => {
    setStatus('checking');
    setErrorMsg('');
    dispatch(fetchEnvs())
      .unwrap()
      .then((data) => {
        if (data.currentUser) {
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMsg(t.noSession);
        }
      })
      .catch(() => {
        setStatus('error');
        setErrorMsg(t.apiFailed);
      });
  };

  const successView = currentUser && status === 'success';

  return (
    <>
      {/* 플로팅 버튼 */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[9999] w-12 h-12 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 active:scale-95 transition-all flex items-center justify-center"
        data-tooltip="Dev Auth Guide"
      >
        <svg
          viewBox="0 0 24 24"
          width={22}
          height={22}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </button>

      {/* 다이얼로그 */}
      {open && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="w-full max-w-4xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* 헤더 + 언어 탭 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold">{t.title}</h2>
                <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
                  {(['ko', 'en'] as const).map((l) => (
                    <button
                      key={l}
                      onClick={() => setLang(l)}
                      className={`px-2.5 py-1 transition-colors ${
                        lang === l
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {l === 'ko' ? '한국어' : 'English'}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
              >
                &#10005;
              </button>
            </div>

            {/* 본문 */}
            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto text-sm text-gray-700">
              {successView ? (
                /* ── 인증 성공 ── */
                <div className="text-center py-4">
                  <div className="text-4xl mb-3" style={{ color: '#22c55e' }}>
                    &#10003;
                  </div>
                  <h3
                    className="text-xl font-bold mb-3"
                    style={{ color: '#22c55e' }}
                  >
                    {t.authSuccess}
                  </h3>
                  <p className="text-gray-600 mb-1">
                    {t.loggedInAs}:{' '}
                    <strong>
                      {currentUser.email ||
                        currentUser.firstName ||
                        currentUser._id}
                    </strong>
                  </p>
                  {currentUser.channels && currentUser.channels.length > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      {currentUser.channels.map((ch, i) => (
                        <div key={i}>
                          {ch.channelType}: {ch.channelName}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* ── 가이드 + 세션 입력 ── */
                <>
                  <p className="text-gray-500">{t.desc}</p>

                  <ol className="list-decimal list-inside space-y-3">
                    <li>
                      {t.stepLogin}
                      <div className="mt-2 flex gap-2 flex-wrap">
                        <a
                          href={getYoutubeAuthUrl({ direct: true })}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 transition-colors"
                        >
                          YouTube
                        </a>
                        <a
                          href={getTwitchAuthUrl({ direct: true })}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition-colors"
                        >
                          Twitch
                        </a>
                      </div>
                    </li>
                    <li>
                      {t.stepGoogleWarning}
                      <img
                        src="/assets/images/dev-auth/google-warning.png"
                        alt="Google Apps not verified warning"
                        className="mt-2 rounded-lg border border-gray-200 shadow-sm w-full"
                      />
                    </li>
                    <li>
                      {t.stepCopy}
                      <img
                        src="/assets/images/dev-auth/cookie-guide.png"
                        alt="DevTools Cookie Guide"
                        className="mt-2 rounded-lg border border-gray-200 shadow-sm w-full"
                      />
                    </li>
                    <li>{t.stepPaste}</li>
                  </ol>

                  {/* 세션 입력 */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={sessionValue}
                      onChange={(e) => setSessionValue(e.target.value)}
                      placeholder={t.sessionPlaceholder}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') applySession();
                      }}
                    />
                    <button
                      onClick={applySession}
                      disabled={
                        status === 'checking' || !sessionValue.trim()
                      }
                      className="px-4 py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors whitespace-nowrap"
                    >
                      {status === 'checking' ? t.checking : t.apply}
                    </button>
                  </div>

                  <div className="p-3 bg-amber-50 rounded-lg text-xs text-amber-800">
                    {t.note}
                  </div>

                  {errorMsg && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                      {errorMsg}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* 푸터 */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-between items-center">
              {!successView && (
                <button
                  onClick={checkCurrentSession}
                  disabled={status === 'checking'}
                  className="text-sm text-blue-600 hover:underline disabled:opacity-50"
                >
                  {status === 'checking' ? t.checking : t.checkSession}
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className={`px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors ${successView ? 'ml-auto' : ''}`}
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
